// src/components/Assets/index.js
// Main Assets layout component with tab navigation

import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../UserContext";
import "./style.css";

const AssetsLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [t] = useTranslation();
  const { userData } = useContext(UserContext);

  // Determine user permissions based on RolePermissions (like Android does)
  const hasAssetsPermission = userData?.RolePermissions?.some(
    permission => permission.module === "Assets" && permission.view
  );

  const hasAssetsRequestsPermission = userData?.RolePermissions?.some(
    permission => permission.module === "Assets Requests" && permission.view
  );

  // User has edit permissions on Assets = Admin (can approve requests AND request assets)
  // User with view permission on Assets Requests = Farmer (can request assets)
  const canApproveRequests = userData?.RolePermissions?.some(
    permission => permission.module === "Assets" && permission.edit
  );

  const isAdmin = canApproveRequests;
  const isFarmer = hasAssetsRequestsPermission && !canApproveRequests;

  // Determine active tab from URL
  const getActiveTab = useCallback(() => {
    const pathSegments = location.pathname.split("/");
    const lastSegment = pathSegments[pathSegments.length - 1];

    if (lastSegment === "permissions") return "permissions";
    if (lastSegment === "expenses") return "expenses";
    if (lastSegment === "requests") return "requests";
    return "list";
  }, [location.pathname]);

  const [activeTab, setActiveTab] = useState(getActiveTab());

  // Update active tab when location changes
  useEffect(() => {
    setActiveTab(getActiveTab());
  }, [getActiveTab]);

  // Handle tab navigation
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/assets/${tab}`);
  };

  return (
    <div className="assets-container">
      {/* Header Section */}
      <div className="assets-header">
        <div>
          <h1 className="header-title">{t("Assets Management")}</h1>
          <p className="header-subtitle">{t("Manage your tools, machinery, and equipment")}</p>
        </div>
      </div>

      {/* Tab Navigation - Role-based visibility */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === "list" ? "active" : ""}`}
          onClick={() => handleTabChange("list")}
        >
          {t("Assets")}
        </button>

        {/* Admin-only tab: Permission Requests (to approve/deny) */}
        {isAdmin && (
          <button
            className={`tab-button ${activeTab === "permissions" ? "active" : ""}`}
            onClick={() => handleTabChange("permissions")}
          >
            {t("Permission Requests")}
          </button>
        )}

        {/* My Requests tab - Available for both Admins and Farmers */}
        {(isAdmin || isFarmer) && (
          <button
            className={`tab-button ${activeTab === "requests" ? "active" : ""}`}
            onClick={() => handleTabChange("requests")}
          >
            {t("My Requests")}
          </button>
        )}

        <button
          className={`tab-button ${activeTab === "expenses" ? "active" : ""}`}
          onClick={() => handleTabChange("expenses")}
        >
          {t("Expenses")}
        </button>
      </div>

      {/* Content Area - Renders child routes */}
      <div className="assets-content">
        <Outlet />
      </div>
    </div>
  );
};

export default AssetsLayout;
